import * as React from "react";

import { useQuery } from "@tanstack/react-query";
import { Check } from "lucide-react";

import {
  checkDealConflicts,
  type Deal,
  type DealConflictMatch,
  type DealConflictResult,
} from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { FormError } from "@/components/ui/form-error";
import { formatAud } from "@/lib/currency";
import { INDUSTRY_LABELS } from "@/lib/deal-labels";
import { cn } from "@/lib/utils";

interface Props {
  deal: Deal;
  accessToken: string;
}

/** Maximum number of matches that can be compared side-by-side (CHP-418). */
const MAX_SELECTED = 2;

/** Render the registering person with their role in brackets (CHP-418 Fix 1).
 * "Alice BDM (BDM)" / "Sam Owner (PO)" — falls back to the bare name, then "—". */
function registeredByLabel(
  m: Pick<DealConflictMatch, "registered_by_name" | "registered_by_role">,
) {
  if (!m.registered_by_name) return "—";
  return m.registered_by_role
    ? `${m.registered_by_name} (${m.registered_by_role})`
    : m.registered_by_name;
}

/** Date-only AU render of an ISO timestamp, "—" when absent. */
function formatRegisteredDate(value: string | null | undefined): string {
  if (!value) return "—";
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? "—" : new Date(ms).toLocaleDateString("en-AU");
}

/** A comparison row: how to label it, render each column, and the normalised
 * value used for the "all columns match" flag (CHP-418 field-matching). */
interface CompareRow {
  label: string;
  /** Display text for the registration under review. */
  registration: (deal: Deal) => string;
  /** Display text for a selected match. */
  match: (m: DealConflictMatch) => string;
  /** Normalised comparison value for the registration (null when absent). */
  registrationKey: (deal: Deal) => string | number | null;
  /** Normalised comparison value for a match (null when absent). */
  matchKey: (m: DealConflictMatch) => string | number | null;
}

/** Case-insensitive, whitespace-trimmed text key (CHP-418). */
function textKey(v: string | null | undefined): string | null {
  if (v == null) return null;
  const trimmed = v.trim().toLowerCase();
  return trimmed === "" ? null : trimmed;
}

function dash(v: string | null | undefined): string {
  return v == null || v.trim() === "" ? "—" : v;
}

/** The comparison field set — mirrors "The Deal" section so the reviewer can
 * compare a match against the registration under review (CHP-418). Currency is
 * compared by value (cents), not formatted string; text is case-insensitive +
 * whitespace-trimmed. */
const COMPARE_ROWS: CompareRow[] = [
  {
    label: "Client",
    registration: (d) => dash(d.client_name),
    match: (m) => dash(m.client_name),
    registrationKey: (d) => textKey(d.client_name),
    matchKey: (m) => textKey(m.client_name),
  },
  {
    // Designer review: registration date, sat directly under the client row.
    // Informational only — not part of the "all columns match" tinting (dates
    // legitimately differ between two registrations), so both keys return null.
    label: "Date registered",
    registration: (d) => formatRegisteredDate(d.submission_timestamp),
    match: (m) => formatRegisteredDate(m.submission_timestamp),
    registrationKey: () => null,
    matchKey: () => null,
  },
  {
    label: "ABN",
    registration: (d) => dash(d.client_abn),
    match: (m) => dash(m.client_abn),
    registrationKey: (d) => textKey(d.client_abn),
    matchKey: (m) => textKey(m.client_abn),
  },
  {
    label: "Lead Contact",
    registration: (d) => dash(d.client_lead_contact),
    match: (m) => dash(m.client_lead_contact),
    registrationKey: (d) => textKey(d.client_lead_contact),
    matchKey: (m) => textKey(m.client_lead_contact),
  },
  {
    label: "Industry",
    registration: (d) => (d.industry_vertical ? INDUSTRY_LABELS[d.industry_vertical] : "—"),
    match: (m) =>
      m.industry_vertical
        ? (INDUSTRY_LABELS[m.industry_vertical as keyof typeof INDUSTRY_LABELS] ??
          m.industry_vertical)
        : "—",
    registrationKey: (d) => textKey(d.industry_vertical),
    matchKey: (m) => textKey(m.industry_vertical),
  },
  {
    label: "Value",
    registration: (d) => formatAud(d.total_contract_value),
    match: (m) => formatAud(m.total_contract_value),
    registrationKey: (d) => d.total_contract_value,
    matchKey: (m) => m.total_contract_value,
  },
  {
    label: "Term",
    registration: (d) =>
      d.proposed_term_months == null ? "—" : `${d.proposed_term_months} months`,
    match: (m) => (m.proposed_term_months == null ? "—" : `${m.proposed_term_months} months`),
    registrationKey: (d) => d.proposed_term_months,
    matchKey: (m) => m.proposed_term_months,
  },
  {
    label: "Closing Date",
    registration: (d) => dash(d.target_close_date),
    match: (m) => dash(m.target_close_date),
    registrationKey: (d) => textKey(d.target_close_date),
    matchKey: (m) => textKey(m.target_close_date),
  },
  {
    label: "Service",
    registration: (d) => dash(d.service_offering),
    match: (m) => dash(m.service_offering),
    registrationKey: (d) => textKey(d.service_offering),
    matchKey: (m) => textKey(m.service_offering),
  },
  {
    label: "GPU Count",
    registration: (d) => (d.estimated_gpu_count == null ? "—" : String(d.estimated_gpu_count)),
    match: (m) => (m.estimated_gpu_count == null ? "—" : String(m.estimated_gpu_count)),
    registrationKey: (d) => d.estimated_gpu_count,
    matchKey: (m) => m.estimated_gpu_count,
  },
  {
    label: "Registered by",
    registration: (d) =>
      d.lead_bdm_name && d.lead_bdm_role
        ? `${d.lead_bdm_name} (${d.lead_bdm_role})`
        : (d.lead_bdm_name ?? "—"),
    match: (m) => registeredByLabel(m),
    registrationKey: (d) => textKey(d.lead_bdm_name),
    matchKey: (m) => textKey(m.registered_by_name),
  },
];

/** A row is flagged when every rendered column shares the same non-null
 * normalised value (CHP-418). A null in any column means "no value to match". */
function rowAllMatch(row: CompareRow, deal: Deal, selected: DealConflictMatch[]): boolean {
  const base = row.registrationKey(deal);
  if (base == null) return false;
  return selected.every((m) => row.matchKey(m) === base);
}

/**
 * CHP-256 / CHP-418: the registration-review ABN Check. The check is ABN-exact
 * and binary — Match Found / No Match Found. Each match is enriched with the
 * conflicting deal's client contact and its registering partner/BDM-PO (with
 * the role in brackets), and the reviewer can select up to two matches to
 * compare against the registration under review in a side-by-side table.
 */
export function DealConflictIndicator({ deal, accessToken }: Props) {
  const dealId = deal.id;
  const conflict = useQuery<DealConflictResult>({
    queryKey: ["deals", "conflict", dealId, accessToken],
    queryFn: () => checkDealConflicts(accessToken, dealId),
    enabled: !!accessToken && !!dealId,
  });

  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [comparisonCollapsed, setComparisonCollapsed] = React.useState(false);

  const matches = conflict.data?.matches ?? [];

  // Keep the selection in sync with the available matches (a refetch could drop
  // a previously-selected match — e.g. the conflicting deal was decided).
  React.useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => matches.some((m) => m.deal_id === id)));
    // matches is derived from query data; key on the stable id list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches.map((m) => m.deal_id).join(",")]);

  function toggle(dealIdToToggle: string) {
    setSelectedIds((prev) => {
      if (prev.includes(dealIdToToggle)) {
        return prev.filter((id) => id !== dealIdToToggle);
      }
      if (prev.length >= MAX_SELECTED) return prev;
      return [...prev, dealIdToToggle];
    });
  }

  if (conflict.isLoading) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Checking the client ABN...
      </p>
    );
  }

  if (conflict.error) {
    return <FormError>Could not run the ABN check.</FormError>;
  }

  if (!conflict.data) return null;

  const matchFound = matches.length > 0;
  const limitReached = selectedIds.length >= MAX_SELECTED;
  // Render order follows the match list so the table columns are stable.
  const selectedMatches = matches.filter((m) => selectedIds.includes(m.deal_id));

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">ABN Check</span>
        <Badge variant={matchFound ? "destructive" : "success"}>
          {matchFound ? "Match Found" : "No Match Found"}
        </Badge>
      </div>

      <p className="text-sm text-muted-foreground">
        Checks for existing registrations under this ABN. Review records to ensure no overlap.
      </p>

      {matchFound ? (
        <>
          <p className="text-xs text-muted-foreground">Select up to 2 to compare.</p>
          <ul className="space-y-2">
            {matches.map((m) => {
              const checked = selectedIds.includes(m.deal_id);
              const disabled = !checked && limitReached;
              return (
                <li
                  key={m.deal_id}
                  className="flex items-start gap-3 rounded-md border border-border p-3 text-sm"
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-input accent-primary disabled:cursor-not-allowed disabled:opacity-40"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => toggle(m.deal_id)}
                    aria-label={`Compare ${m.deal_id_display ?? m.deal_id}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1">
                      <span className="font-medium">{m.deal_id_display ?? m.deal_id}</span>
                      <span className="text-muted-foreground">– {m.client_name}</span>
                      {m.client_lead_contact ? (
                        <span className="text-muted-foreground">– {m.client_lead_contact}</span>
                      ) : null}
                    </div>
                    <p className="mt-1 text-muted-foreground">
                      Registered by: {m.registered_by_company ?? "—"} – {registeredByLabel(m)}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>

          {selectedMatches.length > 0 ? (
            <ComparisonPanel
              deal={deal}
              selectedMatches={selectedMatches}
              collapsed={comparisonCollapsed}
              onToggleCollapse={() => setComparisonCollapsed((prev) => !prev)}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function ComparisonPanel({
  deal,
  selectedMatches,
  collapsed,
  onToggleCollapse,
}: {
  deal: Deal;
  selectedMatches: DealConflictMatch[];
  collapsed: boolean;
  onToggleCollapse: () => void;
}) {
  return (
    <div className="rounded-md border border-border">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <span className="text-sm font-medium">Comparison</span>
        <button
          type="button"
          onClick={onToggleCollapse}
          className="text-xs font-medium text-primary underline-offset-4 hover:underline"
        >
          {collapsed ? "Show comparison" : "Hide comparison"}
        </button>
      </div>
      {!collapsed ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                {/* Designer review: the row-label column carries no header title. */}
                <th className="px-3 py-2 font-semibold text-muted-foreground" aria-hidden />
                {/* Designer review: the anchor column is tinted so the reviewer
                    can see at a glance which column the matches compare against. */}
                <th className="border-x border-primary/20 bg-primary/5 px-3 py-2 font-semibold">
                  This registration
                </th>
                {selectedMatches.map((m) => (
                  <th key={m.deal_id} className="px-3 py-2 font-semibold">
                    {m.deal_id_display ?? m.deal_id}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map((row) => {
                const allMatch = rowAllMatch(row, deal, selectedMatches);
                // CHP-1079: a row "differs" only when it is comparable —
                // informational rows (null keys, e.g. Date registered) are
                // neither matching nor differing, so they get no accent.
                const base = row.registrationKey(deal);
                const differs = base != null && !allMatch;
                return (
                  <tr
                    key={row.label}
                    className={cn("border-b border-border last:border-0", allMatch && "bg-muted")}
                  >
                    {/* CHP-1079: differing rows carry a red left-edge accent;
                        the bolded differing cells below are the non-colour cue
                        (CHP-807 a11y), plus a visually-hidden label. */}
                    <td
                      className={cn(
                        "px-3 py-2 align-top font-semibold text-muted-foreground",
                        differs && "border-l-4 border-l-destructive",
                      )}
                    >
                      {row.label}
                    </td>
                    {/* Anchor column — subtly tinted to match the header. A
                        non-colour cue (check icon + visually-hidden label) marks
                        all-match rows so the meaning isn't carried by the
                        bg-muted tint alone (CHP-807 a11y). */}
                    <td className="border-x border-primary/20 bg-primary/5 px-3 py-2 align-top">
                      <span className="inline-flex items-center gap-1.5">
                        {allMatch ? (
                          <>
                            <Check className="h-3.5 w-3.5 shrink-0 text-success" aria-hidden />
                            <span className="sr-only">All values match. </span>
                          </>
                        ) : null}
                        {differs ? <span className="sr-only">Values differ. </span> : null}
                        <span>{row.registration(deal)}</span>
                      </span>
                    </td>
                    {selectedMatches.map((m) => {
                      // CHP-1079: bold the cells that differ from the anchor.
                      const cellDiffers = base != null && row.matchKey(m) !== base;
                      return (
                        <td
                          key={m.deal_id}
                          className={cn("px-3 py-2 align-top", cellDiffers && "font-semibold")}
                        >
                          {row.match(m)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
